import { type ReactNode } from "react";

import { Button } from "#components/Button";
import {
  CheckCircleFillIcon,
  ClearIcon,
  InfoCheckFillIcon,
  LightbulbFillIcon,
  NegativeSolidIcon,
  WarningCircleFillIcon,
} from "#icons";
import { cn, customTv } from "#utils";

import type { VariantProps } from "tailwind-variants";

export type AlertSeverity = "success" | "info" | "warning" | "error" | "secondary";

/** AlertProps interface for the Alert component */
export interface AlertProps extends VariantProps<typeof alertStyles> {
  /** The content of the alert */
  children: ReactNode;
  /** The severity of the alert - determines color and default icon */
  severity?: AlertSeverity;
  /** Visual variant - "normal" (filled background) or "light" (white bg with colored border) */
  variant?: "normal" | "light";
  /** Custom icon to override the default severity icon. Set to false to remove icon */
  icon?: ReactNode | false;
  /** Action element to display at the end of the alert */
  action?: ReactNode;
  /** Callback fired when close button is clicked */
  onClose?: () => void;
  /** Override the default label for the close button */
  closeText?: string;
  /** Additional CSS classes */
  className?: string;
  /** ARIA role for the alert */
  role?: string;
}

const alertStyles = customTv({
  base: "flex flex-col gap-1 p-2 rounded-md text-text-primary text-label-medium",
  variants: {
    severity: {
      success: "",
      info: "",
      warning: "",
      error: "",
      secondary: "",
    },
    variant: {
      normal: "",
      light: "",
    },
  },
  compoundVariants: [
    // Normal (filled backgrounds — semantic tokens so they follow the theme)
    {
      severity: "success",
      variant: "normal",
      class: "bg-background-positive",
    },
    {
      severity: "info",
      variant: "normal",
      class: "bg-background-informative",
    },
    {
      severity: "warning",
      variant: "normal",
      class: "bg-background-warning",
    },
    {
      severity: "error",
      variant: "normal",
      class: "bg-background-negative",
    },
    {
      severity: "secondary",
      variant: "normal",
      class: "bg-background-secondary",
    },

    // Light (themed surface with a border matching the icon color)
    {
      severity: "success",
      variant: "light",
      class: "bg-background-primary border border-positive-600",
    },
    {
      severity: "info",
      variant: "light",
      class: "bg-background-primary border border-informative-600",
    },
    {
      severity: "warning",
      variant: "light",
      class: "bg-background-primary border border-warning-500",
    },
    {
      severity: "error",
      variant: "light",
      class: "bg-background-primary border border-negative-600",
    },
    {
      severity: "secondary",
      variant: "light",
      class: "bg-background-primary border border-neutral-700",
    },
  ],
  defaultVariants: {
    severity: "success",
    variant: "normal",
  },
});

const iconStyles = customTv({
  base: "flex-shrink-0 w-6 h-6",
  variants: {
    severity: {
      success: "text-positive-600",
      info: "text-informative-600",
      warning: "text-warning-500",
      error: "text-negative-600",
      secondary: "text-foreground-secondary",
    },
  },
});

// Default icon mapping for each severity
const defaultIcons: Record<AlertSeverity, ReactNode> = {
  success: <CheckCircleFillIcon className="h-6 w-6" />,
  info: <InfoCheckFillIcon className="h-6 w-6" />,
  warning: <WarningCircleFillIcon className="h-6 w-6" />,
  error: <NegativeSolidIcon className="h-6 w-6" />,
  secondary: <LightbulbFillIcon className="h-6 w-6" />,
};

export function Alert({
  children,
  severity = "success",
  variant = "normal",
  icon,
  action,
  onClose,
  closeText = "Close",
  className,
  role = "alert",
}: AlertProps) {
  // Determine which icon to show
  const shouldShowIcon = icon !== false;
  const iconToShow = icon || (shouldShowIcon ? defaultIcons[severity] : null);

  return (
    <div className={cn(alertStyles({ severity, variant }), className)} role={role}>
      <div className="flex gap-2">
        {iconToShow ? <div className={iconStyles({ severity })}>{iconToShow}</div> : null}

        <div className="flex flex-grow flex-col items-start justify-center">
          <div>{children}</div>
        </div>

        {onClose ? (
          <div className="flex-shrink-0">
            <Button
              aria-label={closeText}
              className="border-none p-1"
              onPress={onClose}
              variant="tertiary"
            >
              <ClearIcon className="h-4 w-4" />
            </Button>
          </div>
        ) : null}
      </div>
      {action ? <div className="flex justify-end">{action}</div> : null}
    </div>
  );
}
